import {
  checkNodeVisibility,
  IInputNode,
  ActionCardModel,
  BaseNode,
  QuestionNode,
  EmailQuestionNode,
  AssessmentNode,
} from '../actionCard'

export class NodeSearchService implements Iterable<BaseNode> {
  private actionCardSource?: ActionCardModel
  private nodesSource: ActionCardModel | Iterable<BaseNode> | null | undefined
  private filterFunc?: (node: BaseNode) => boolean

  constructor(
    actionCardSource?: ActionCardModel,
    nodesSource?: ActionCardModel | Iterable<BaseNode> | null | undefined,
    filterFunc?: (node: BaseNode) => boolean,
  ) {
    this.actionCardSource = actionCardSource
    this.nodesSource = nodesSource ?? actionCardSource
    this.filterFunc = filterFunc
  }

  *[Symbol.iterator]() {
    for (const node of iterateThroughNodesSource(this.nodesSource)) {
      if (this.filterFunc) {
        if (this.filterFunc(node)) {
          yield node
        }
      } else {
        yield node
      }
    }
  }

  public filter(filterFunc: (node: BaseNode) => boolean): NodeSearchService {
    return new NodeSearchService(this.actionCardSource, this, filterFunc)
  }

  public filterVisible(): NodeSearchService {
    return this.filter(x => {
      return checkNodeVisibility(x, this.actionCardSource)
    })
  }

  public filterQuestions(): NodeSearchService {
    return this.filter(x => x instanceof QuestionNode)
  }

  public filterEmails(): NodeSearchService {
    return this.filter(x => {
      return x instanceof EmailQuestionNode
    })
  }

  public findEmail(): NodeSearchService {
    return this.find(x => {
      return x instanceof EmailQuestionNode
    })
  }

  public filterNotEmptyInputNodes(): NodeSearchService {
    return this.filter(x => {
      const inputNode = x as unknown as IInputNode<any>
      return inputNode.IInputNode && !inputNode.isInputEmpty()
    })
  }

  public filterEmptyInputNodes(): NodeSearchService {
    return this.filter(x => {
      const inputNode = x as unknown as IInputNode<any>
      return inputNode.IInputNode && inputNode.isInputEmpty()
    })
  }

  public filterRequired(): NodeSearchService {
    return this.filter(x => {
      return !!(x as QuestionNode | AssessmentNode).data.required
    })
  }

  find<T extends BaseNode>(filterFunc: (node: BaseNode) => node is T): T | any
  find<T extends BaseNode>(filterFunc: (node: BaseNode) => boolean): T | any
  find<T extends BaseNode>(): T | any

  public find(findFunc?: (node: BaseNode) => boolean): any {
    for (const node of this) {
      if (!findFunc || findFunc(node)) {
        return node
      }
    }
  }

  public findById<T extends BaseNode>(id: number): T | any {
    return this.find(x => x.getId() === id) as T | any
  }

  public findQuestionByCode(code: string): QuestionNode | any {
    return this.filterQuestions().find(x => {
      const questionNode = x as QuestionNode
      return questionNode.getCode() === code
    })
  }

  public every(everyFunc: (node: BaseNode) => boolean): boolean {
    // true must be returned only if at least one item exists
    let result = null
    for (const node of this) {
      result = (result ?? true) && (!everyFunc || !!everyFunc(node))
    }
    return !!result
  }

  public some(someFunc: (node: BaseNode) => boolean): boolean {
    // true must be returned only if at least one item exists
    for (const node of this) {
      if (!someFunc || someFunc(node)) {
        return true
      }
    }
    return false
  }

  public map(mapFunc: (node: BaseNode) => any): any[] | undefined {
    if (!mapFunc) {
      return
    }
    const result = []
    for (const node of this) {
      result.push(mapFunc(node))
    }

    return result
  }

  public forEach<T extends BaseNode>(forEachFunc: (node: T) => void) {
    if (!forEachFunc) {
      return
    }
    for (const node of this) {
      forEachFunc(node as T)
    }
  }

  toGenerator<T extends BaseNode>(filterFunc: (node: BaseNode) => node is T): Generator<T>
  toGenerator<T extends BaseNode>(filterFunc: (node: BaseNode) => boolean): Generator<T>
  toGenerator<T extends BaseNode>(): Generator<T>

  public *toGenerator(filterFunc?: (node: BaseNode) => boolean): Generator<BaseNode> {
    for (const node of this) {
      if (!filterFunc || filterFunc(node)) {
        yield node
      }
    }
  }

  toArray<T extends BaseNode>(filterFunc: (node: BaseNode) => node is T): T[]
  toArray<T extends BaseNode>(filterFunc: (node: BaseNode) => boolean): T[]
  toArray<T extends BaseNode>(): T[]

  public toArray(filterFunc?: (node: BaseNode) => boolean): BaseNode[] {
    return filterFunc ? [...this.toGenerator(filterFunc)] : [...this.toGenerator()]
  }

  public toSearchService(): NodeSearchService {
    return new NodeSearchService(this.actionCardSource, [...this])
  }
}

function* iterateThroughNodesSource(
  nodesSource: ActionCardModel | Iterable<BaseNode> | null | undefined,
): Iterable<BaseNode> {
  yield* iterateThroughActionCardModel(nodesSource)
  yield* iterateThroughIterator(nodesSource)
}

function* iterateThroughActionCardModel(
  nodesSource: ActionCardModel | Iterable<BaseNode> | null | undefined,
): Iterable<BaseNode> {
  const nodes = (nodesSource as ActionCardModel)?.rootNodes
  yield* traverseNodes(nodes)
}

function* iterateThroughIterator(
  nodesSource: ActionCardModel | Iterable<BaseNode> | null | undefined,
): Iterable<BaseNode> {
  if (!nodesSource || typeof (nodesSource as Iterable<BaseNode>)[Symbol.iterator] !== 'function') {
    return
  }
  const nodes = nodesSource as Iterable<BaseNode>
  for (const node of nodes) {
    yield node
  }
}

function* traverseNodes(nodes: BaseNode[] | null | undefined): Iterable<BaseNode> {
  if (!nodes || nodes.length === 0) {
    return
  }
  for (const node of nodes) {
    yield node
    yield* traverseNodes(node.children)
  }
}
